/** WF category options every site gets. */
export const DEFAULT_CATEGORY_OPTIONS = [
  { label: "1", value: "1" },
  { label: "2", value: "2" },
  { label: "3", value: "3" },
  { label: "RC", value: "RC" },
];

/**
 * Per-hospital WF category options. A site listed here REPLACES the default
 * list, so spread DEFAULT_CATEGORY_OPTIONS in unless the site genuinely drops
 * a shared value.
 *
 * TP (transfer patient) means the hospital has already seen the patient face to
 * face and assigns the appointment date. MNHHS is the site it was requested for;
 * the development envs are listed too so it can be exercised there.
 *
 * This map decides what an admin can SELECT. It is not an enforcement boundary
 * and must not be read as one: nothing server-side rejects TP, so a TP value
 * arriving by any other route is stored as-is. That is not hypothetical — OPAU
 * offers TP to every organisation, and rebuild-workflow / track-patient pull the
 * referral back from PMS. Enforcement, if it is ever wanted, belongs at the
 * workflow API write boundary, not here.
 */
const WITH_TP = [...DEFAULT_CATEGORY_OPTIONS, { label: "TP", value: "TP" }];

export const CATEGORY_OPTIONS_BY_HOSPITAL = {
  METRO_NORTH_HHS: WITH_TP,
  // MACKAY_EYE is the hospital name the development envs use
  // (.env.development, .env.pms.development), so this is what gives dev TP.
  // Mackay Base Hospital itself is MACKAY below.
  MACKAY_EYE: WITH_TP,
  // Mackay Base Hospital (.env.mackay). PDR is Mackay's own category (SE-127).
  MACKAY: [...WITH_TP, { label: "PDR", value: "PDR" }],
};

export const getCategoryOptions = (hospitalName) =>
  (hospitalName && CATEGORY_OPTIONS_BY_HOSPITAL[hospitalName]) || DEFAULT_CATEGORY_OPTIONS;

/**
 * Per-hospital options for the appointment status dropdown.
 * Hospitals not listed here use the default (_DEFAULT_OPTIONS in constants.js).
 */
export const WORKFLOW_APPT_STATUS_OPTIONS_BY_HOSPITAL = {
  WIDE_BAY: [
    { label: "Audit", value: "adminAudit" },
    { label: "Booked", value: "booked" },
    { label: "Check Out", value: "checkOut" },
    { label: "Discharged", value: "discharged" },
    { label: "Expired", value: "expiredReferral" },
    { label: "DNA", value: "dna" },
    { label: "DNA Rebook", value: "dnaRebook" },
    { label: "DNA Removed", value: "dnaRemoved" },
    { label: "F2F Transfer", value: "f2fTransfer" },
    { label: "Late Cancel", value: "lateCancel" },
    { label: "NRFC", value: "nrfc" },
    { label: "Removed", value: "remove" },
    { label: "Relocated", value: "relocated" },
    { label: "To Book", value: "awaitingBooking" },
  ],
  METRO_NORTH_HHS: [
    { label: "Audit", value: "adminAudit" },
    { label: "Booked", value: "booked" },
    { label: "Check Out", value: "checkOut" },
    { label: "Discharged", value: "discharged" },
    { label: "Expired", value: "expiredReferral" },
    { label: "DNA", value: "dna" },
    { label: "DNA Rebook", value: "dnaRebook" },
    { label: "DNA Removed", value: "dnaRemoved" },
    { label: "F2F Transfer", value: "f2fTransfer" },
    { label: "Late Cancel", value: "lateCancel" },
    { label: "NRFC", value: "nrfc" },
    { label: "Removed", value: "remove" },
    { label: "Relocated", value: "relocated" },
    { label: "To Book", value: "awaitingBooking" },
  ],
};

/**
 * Per-hospital room lists. Each entry is `{ key, label, locationId? }`, where
 * locationId is the PMS location UUID — set it when using pms booking
 * availability for calendar colours.
 */
export const ROOM_LIST = {
  WIDE_BAY: [
    { key: 1, label: "Room 1: Bundaberg Hospital" },
    { key: 2, label: "Room 2: Hervey Bay Hospital" },
    { key: 3, label: "Room 3: Maryborough Hospital" },
    { key: 4, label: "Room 4: Own Device" },
  ],
  MACKAY_EYE: [
    { key: 1, label: "Room 1: Rural Clinic" },
    { key: 2, label: "Room 2: Main" },
    { key: 3, label: "Room 3: Liver" },
    { key: 4, label: "Room 4: Own Device", locationId: "7ab813d0-ac0b-41c2-89f2-604d256a1c26" }, // PMS location UUID for testing purpose
  ],
  // Mackay Base Hospital (.env.mackay): MACKAY_EYE's rooms without the PMS
  // locationId, since Mackay books through Coreplus.
  MACKAY: [
    { key: 1, label: "Room 1: Rural Clinic" },
    { key: 2, label: "Room 2: Main" },
    { key: 3, label: "Room 3: Liver" },
    { key: 4, label: "Room 4: Own Device" },
  ],
  CENTRAL_QLD_HHS: [{ key: 1, label: "Room 1: SOPD", locationId: "8d4a4ca3-2c91-493e-a1e3-b6fbf5b74764" }],
  MB_PSYCHOLOGY: [{ key: 1, label: "Room 1: Own Device" }],
  // TODO: Update this
  CAIRNS_HINTERLAND_HHS: [{ key: 1, label: "Room 1: Own Device" }],
  RURAL_HEALTH_CONNECT: [{ key: 1, label: "Room 1: Own Device" }],
  METRO_NORTH_HHS: [{ key: 1, label: "Room 1: Own Device" }],
};
